import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { EntityForm } from "@/components/v2/entity-form";
import { EmptyState } from "@/components/v2/empty-state";
import { useCreateInvoice, useCustomers } from "@/hooks/use-api";
import { invoiceFields, type InvoiceFormValues } from "./invoice-form-config";

export default function V2InvoiceCreatePage() {
  const navigate = useNavigate();
  const createInvoice = useCreateInvoice();
  const customersQuery = useCustomers({ limit: 1000 });
  const [error, setError] = useState<string | null>(null);

  const customerOptions = (
    (
      customersQuery.data as
        | { customers?: Record<string, unknown>[] }
        | undefined
    )?.customers ?? []
  ).map((c) => ({
    value: String(c.contact_role_id ?? ""),
    label:
      [c.first_name, c.last_name].filter(Boolean).join(" ") ||
      String(c.company_name ?? ""),
  }));

  async function onSubmit(values: InvoiceFormValues) {
    setError(null);
    try {
      const { invoice } = await createInvoice.mutateAsync(
        values as Record<string, unknown>,
      );
      const id = (invoice as Record<string, unknown> | undefined)?.id;
      if (!id) {
        setError("Unexpected response from the server — please try again.");
        return;
      }
      toast.success("Invoice created");
      navigate(`/invoices/${id}`);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Could not create the invoice — check the fields and try again.",
      );
    }
  }

  // The customer select is required — surface the lookup's loading/error
  // states instead of silently rendering an empty select.
  if (customersQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="size-6 animate-spin text-[color:var(--color-text-lo)]" />
      </div>
    );
  }

  if (customersQuery.isError) {
    return (
      <EmptyState
        title="Could not load customers"
        description="The customer list failed to load, so the invoice form can't be filled in yet."
        action={{ label: "Try again", onClick: () => customersQuery.refetch() }}
      />
    );
  }

  return (
    <EntityForm<InvoiceFormValues>
      title="Create invoice"
      breadcrumbs={[{ label: "Invoices", href: "/invoices" }, { label: "New" }]}
      defaultValues={{ status: "draft" }}
      fields={invoiceFields(customerOptions)}
      submitLabel="Create invoice"
      onSubmit={onSubmit}
      cancelHref="/invoices"
      error={error}
      isSubmitting={createInvoice.isPending}
    />
  );
}
